import React, { useContext, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import Button from "shared/components/FormElements/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import SmsSenderAccountSelect, { DEFAULT_SMS_MSG_HEADER } from "shared/components/SmsSenderAccountSelect";
import SmsRow from "./SmsRow";
import { isInternationalTheme } from "config/ConfigUtils";

import { SMS_STATUS_CODES } from "../constants";

const MAX_SMS_LENGTH = 1000;

const SmsTable = ({ users, driver, serviceId, model = "personelservice", extraInfo = null }) => {
  const { t } = useTranslation();

  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);

  const [currentSmses, setCurrentSmses] = React.useState([]);

  const [isSmsSending, setIsSmsSending] = React.useState(false);
  const [isSmsesLoaded, setIsSmsesLoaded] = React.useState(false);
  const [msgHeader, setMsgHeader] = React.useState(DEFAULT_SMS_MSG_HEADER);

  const loadSMS = useCallback(async () => {
    console.log("LoadSms has been called");
    if (isSmsesLoadedRef.current) {
      const hasPendingSms = smsRef.current.some((sms) => sms.status === SMS_STATUS_CODES.QUEUED);
      const noSmsAtAll = smsRef.current.length === 0;

      console.log("hasPendingSms:", hasPendingSms);
      console.log("noSmsAtAll:", noSmsAtAll);
      if (noSmsAtAll || !hasPendingSms) {
        return;
      }
    }
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/smsv2/getsmsbymodel`,
        "POST",
        JSON.stringify({
          model: model,
          modelId: serviceId,
        }),
      );
      setCurrentSmses(responseData.smses);
    } catch (err) {}
    setIsSmsesLoaded(true);
  }, [sendRequest, serviceId]);

  const fetchItem = useCallback(async () => {
    try {
      await loadSMS();
    } catch (err) {}
  }, [loadSMS]);

  useEffect(() => {
    fetchItem();

    const intervalId = setInterval(() => {
      fetchItem();
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchItem]);

  const smsRef = useRef([]);
  const isSmsesLoadedRef = useRef(false);
  useEffect(() => {
    smsRef.current = currentSmses;
    isSmsesLoadedRef.current = isSmsesLoaded;
  }, [currentSmses, isSmsesLoaded]);

  // ESKİ YÖNTEM (geri dönüş gerekirse): her kullanıcı için ayrı /smsv2 isteği atar.
  // Sorun çıkarsa aşağıdaki yeni SendSMSToAll'u commente alıp bunu açman yeterli.
  // const SendSMSToAll = async () => {
  //   setIsSmsSending(true);
  //   const usersToSend = users.map((user) => user?.id);
  //   const promises = usersToSend.map((userId) => SendSMS(userId, true));
  //   await Promise.all(promises);
  //   setIsSmsSending(false);
  // };

  const SendSMSToAll = async () => {
    const message = document.getElementById("textAll").value.trim();
    if (message.length === 0) {
      alert("Mesaj boş olamaz!");
      return;
    }

    // Telefonu olmayan kullanıcılar eskiden de sessizce atlanıyordu; aynı davranış korunuyor.
    const recipients = users
      .filter((user) => user?.id && typeof user?.phonenumber === "string" && user.phonenumber.length > 0)
      .map((user) => ({
        user: user.id.toString(),
        phoneNumber: user.phonenumber,
      }));

    if (recipients.length === 0) {
      return;
    }

    setIsSmsSending(true);
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/smsv2/bulk-by-model`,
        "POST",
        JSON.stringify({
          recipients,
          text: message,
          model,
          modelId: serviceId,
          ...(!isInternationalTheme() && { msgHeader }),
        }),
      );

      if (responseData?.smses?.length) {
        setCurrentSmses((prev) => [...prev, ...responseData.smses]);
        document.getElementById("textAll").value = "";
      }
    } catch (err) {}
    setIsSmsSending(false);
  };

  const SendSMS = async (userId, isMultiple, isDriver = false) => {
    setIsSmsSending(true);
    const textareaId = "text" + (isMultiple ? "All" : userId);
    const message = document.getElementById(textareaId).value.trim();
    if (message.length === 0) {
      alert("Mesaj boş olamaz!");
      setIsSmsSending(false);
      return;
    }

    const foundEntity = isDriver ? driver : users.find((user) => user?.id?.toString() === userId.toString());

    const phoneNumber = foundEntity.phonenumber;
    if (phoneNumber.length === 0) {
      setIsSmsSending(false);
      return;
    }
    const text = message;

    const smsData = {
      user: userId.toString(),
      phoneNumber: phoneNumber,
      text,
      model,
      modelId: serviceId,
      ...(!isInternationalTheme() && { msgHeader }),
    };

    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/smsv2`,
        "POST",
        JSON.stringify(smsData),
      );

      if (responseData.sms) {
        setCurrentSmses((prev) => [...prev, responseData.sms]);
        document.getElementById(textareaId).value = "";
      }
    } catch (err) {}
    setIsSmsSending(false);
  };

  return (
    <div style={{ margin: "0 180px", marginTop: "30px" }}>
      {!isInternationalTheme() && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: "#fafbfc",
            borderColor: "#e0e4e8",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, maxWidth: "720px" }}>
            {t("smsSenderAccountPageHint")}
          </Typography>
          <SmsSenderAccountSelect
            value={msgHeader}
            onChange={setMsgHeader}
            disabled={isSmsSending}
            sx={{ maxWidth: 400 }}
          />
        </Paper>
      )}

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 3,
          backgroundColor: "#f5f7fa",
          borderColor: "#dfe3e8",
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, color: "#1a1a1a", mb: 0.5 }}>
          {t("bulkSms")}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, maxWidth: "720px" }}>
          {t("smsBulkSectionHint")}
        </Typography>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: "12px",
            }}
          >
            <label
              htmlFor="textAll"
              style={{
                fontSize: "12px",
                color: "rgba(0, 0, 0, 0.6)",
                lineHeight: 1.3,
                fontWeight: 500,
              }}
            >
              {t("messageText")}
            </label>
            <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
              {MAX_SMS_LENGTH} {t("smsCharacterCount")}
            </Typography>
          </div>
          <textarea
            id="textAll"
            rows={5}
            maxLength={MAX_SMS_LENGTH}
            placeholder={t("typeYourMessage")}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: "100px",
              boxSizing: "border-box",
              backgroundColor: "#fff",
              outline: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#3f51b5";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "4px" }}>
            <Button
              type="button"
              style={{ minWidth: "280px", maxWidth: "100%" }}
              disabled={isSmsSending}
              danger
              onClick={() => SendSMSToAll()}
            >
              {t("sendBulkSms")}
            </Button>
          </div>
        </div>
      </Paper>

      {driver && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: "#f8f9ff",
            borderColor: "#c5cae9",
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, color: "#283593", mb: 0.5 }}>
            {t("smsDriverSectionTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, maxWidth: "720px" }}>
            {t("smsDriverSectionHint")}
          </Typography>
          <TableContainer sx={{ overflow: "auto" }}>
            <Table size="medium" sx={{ width: "100%" }} aria-label={t("smsDriverSectionTitle")}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#e8eaf6" }}>
                  <TableCell
                    width="20%"
                    sx={{
                      padding: "12px 16px",
                      verticalAlign: "top",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    <b>{t("user.title")}</b>
                  </TableCell>
                  <TableCell
                    width="35%"
                    sx={{
                      padding: "12px 16px",
                      verticalAlign: "top",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    <b>{t("sentMessages")}</b>
                  </TableCell>
                  <TableCell
                    width="35%"
                    align="right"
                    sx={{
                      padding: "12px 16px",
                      verticalAlign: "top",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    <b>{t("smsIndividualColumnHeader")}</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <SmsRow
                  entity={driver}
                  currentSmses={currentSmses.filter((sms) => sms?.user?.toString() === driver?.id?.toString())}
                  timezone={auth.timezone || "Europe/Istanbul"}
                  onSendSMS={SendSMS}
                  isSmsSending={isSmsSending}
                  isDriver={true}
                  isSmsesLoaded={isSmsesLoaded}
                  maxSmsLength={MAX_SMS_LENGTH}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, color: "#1a1a1a", mb: 1.5, mt: 0.5 }}>
        {t("smsPassengersCardTitle")}
      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          paddingX: 4,
          paddingY: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          backgroundColor: "#fff",
          border: "1px solid #eceff1",
        }}
      >
        <Table
          sx={{ width: "100%", justifyContent: "center", margin: "auto" }}
          stickyHeader
          aria-label={t("smsPassengersCardTitle")}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
              <TableCell
                width="20%"
                sx={{ padding: "16px", verticalAlign: "top", fontWeight: "bold", color: "#333", fontSize: "15px" }}
              >
                <b>{t("user.title")}</b>
              </TableCell>
              <TableCell
                width="35%"
                sx={{ padding: "16px", verticalAlign: "top", fontWeight: "bold", color: "#333", fontSize: "15px" }}
              >
                <b>{t("sentMessages")}</b>
              </TableCell>
              <TableCell
                width="35%"
                align="right"
                sx={{ padding: "16px", verticalAlign: "top", fontWeight: "bold", color: "#333", fontSize: "15px" }}
              >
                <b>{t("smsIndividualColumnHeader")}</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <SmsRow
                key={user?.id}
                entity={user}
                currentSmses={currentSmses.filter((sms) => sms?.user?.toString() === user?.id?.toString())}
                timezone={auth.timezone || "Europe/Istanbul"}
                onSendSMS={SendSMS}
                isSmsSending={isSmsSending}
                isSmsesLoaded={isSmsesLoaded}
                maxSmsLength={MAX_SMS_LENGTH}
                extraInfo={extraInfo ? extraInfo.find((info) => info?.userId === user?.id)?.text : null}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SmsTable;
